import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, Trash2, Download, AlertCircle, X, Eye } from 'lucide-react';
import { DocumentPreview, isPreviewable } from './DocumentPreview';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_EXTENSIONS = ['.pdf', '.doc', '.docx', '.jpg', '.jpeg', '.png', '.gif', '.xls', '.xlsx'];

const getFileIcon = (filename) => {
  const ext = filename.toLowerCase().split('.').pop();
  if (['pdf'].includes(ext)) return '📄';
  if (['doc', 'docx'].includes(ext)) return '📝';
  if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return '🖼️';
  if (['xls', 'xlsx'].includes(ext)) return '📊';
  return '📎';
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const DocumentUpload = ({ loadId, documents = [], onDocumentsChange, disabled = false }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const validateFile = (file) => {
    const ext = '.' + file.name.toLowerCase().split('.').pop();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `File type ${ext} not allowed. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size is 10MB`;
    }
    return null;
  };

  const uploadFile = async (file) => {
    const error = validateFile(file);
    if (error) {
      toast.error(error);
      return null;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/loads/${loadId}/documents`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );
      return response.data;
    } catch (err) {
      const message = err.response?.data?.detail || 'Upload failed';
      toast.error(message);
      return null;
    }
  };

  const handleFiles = async (files) => {
    if (documents.length + files.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} files allowed. You can upload ${MAX_FILES - documents.length} more.`);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    const newDocs = [];
    for (const file of files) {
      const result = await uploadFile(file);
      if (result) {
        newDocs.push(result);
      }
    }

    if (newDocs.length > 0) {
      toast.success(`${newDocs.length} file(s) uploaded successfully`);
      onDocumentsChange?.([...documents, ...newDocs]);
    }

    setUploading(false);
    setUploadProgress(0);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled || uploading) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFiles(files);
    }
  }, [disabled, uploading, documents]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFiles(files);
    }
    e.target.value = '';
  };

  const handleDelete = async (docId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/loads/${loadId}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document deleted');
      onDocumentsChange?.(documents.filter(d => d.id !== docId));
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/loads/${loadId}/documents/${doc.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download document');
    }
  };

  const handlePreview = (doc) => {
    setPreviewDoc(doc);
    setShowPreview(true);
  };

  const handleNavigatePreview = (doc) => {
    setPreviewDoc(doc);
  };

  return (
    <div className="space-y-4" data-testid="document-upload">
      {/* Upload Area */}
      {documents.length < MAX_FILES && !disabled && (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragActive 
              ? 'border-[#00A6E8] bg-[#00A6E8]/5' 
              : 'border-white/20 hover:border-white/40'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          {uploading ? (
            <div className="space-y-2">
              <div className="w-full bg-[#1E293B] rounded-full h-2">
                <div 
                  className="bg-[#00A6E8] h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-slate-400 text-sm">Uploading... {uploadProgress}%</p>
            </div>
          ) : (
            <>
              <Upload className="mx-auto text-slate-400 mb-3" size={32} />
              <p className="text-white font-semibold mb-1">
                Drop files here or click to upload
              </p>
              <p className="text-slate-500 text-sm mb-3">
                PDF, DOC, DOCX, JPG, PNG, XLS (max 10MB each)
              </p>
              <input
                type="file"
                id={`file-upload-${loadId}`}
                className="hidden"
                multiple
                accept={ALLOWED_EXTENSIONS.join(',')}
                onChange={handleFileSelect}
              />
              <label htmlFor={`file-upload-${loadId}`}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-[#00A6E8] text-[#00A6E8] cursor-pointer"
                  asChild
                >
                  <span>Select Files</span>
                </Button>
              </label>
              <p className="text-slate-600 text-xs mt-2">
                {documents.length}/{MAX_FILES} files uploaded
              </p>
            </>
          )}
        </div>
      )}

      {/* File limit reached message */}
      {documents.length >= MAX_FILES && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 flex items-center gap-2">
          <AlertCircle className="text-yellow-400 flex-shrink-0" size={16} />
          <p className="text-yellow-400 text-sm">
            Maximum {MAX_FILES} files reached. Delete a file to upload more.
          </p>
        </div>
      )}

      {/* Document List */}
      {documents.length > 0 && (
        <div className="space-y-2">
          <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">
            Uploaded Documents ({documents.length}/{MAX_FILES})
          </p>
          {documents.map((doc) => (
            <div 
              key={doc.id} 
              className="flex items-center justify-between bg-[#1E293B]/50 border border-white/10 rounded-lg p-3 hover:bg-[#1E293B]/70 transition-colors"
              data-testid={`document-item-${doc.id}`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <span className="text-2xl flex-shrink-0">
                  {getFileIcon(doc.original_filename)}
                </span>
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">
                    {doc.original_filename}
                  </p>
                  <p className="text-slate-500 text-xs">
                    {formatFileSize(doc.file_size)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {/* Preview button - only for previewable files */}
                {isPreviewable(doc.original_filename) && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-[#FFA726] hover:text-[#FFA726]/80 hover:bg-[#FFA726]/10 h-8 w-8 p-0"
                    onClick={() => handlePreview(doc)}
                    title="Preview"
                    data-testid={`preview-btn-${doc.id}`}
                  >
                    <Eye size={16} />
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-[#00A6E8] hover:text-[#00A6E8]/80 h-8 w-8 p-0"
                  onClick={() => handleDownload(doc)}
                  title="Download"
                >
                  <Download size={16} />
                </Button>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-400 hover:text-red-300 hover:bg-red-400/10 h-8 w-8 p-0"
                    onClick={() => handleDelete(doc.id)}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Modal */}
      <DocumentPreview
        document={previewDoc}
        loadId={loadId}
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        documents={documents.filter(d => isPreviewable(d.original_filename))}
        onNavigate={handleNavigatePreview}
      />
    </div>
  );
};

// Simpler version for form submission (before load is created)
export const DocumentUploadPending = ({ files, onChange, disabled = false }) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const validateFile = (file) => {
    const ext = '.' + file.name.toLowerCase().split('.').pop();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `File type ${ext} not allowed`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large (max 10MB)`;
    }
    return null;
  };

  const handleFiles = (newFiles) => {
    if (files.length + newFiles.length > MAX_FILES) {
      toast.error(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    const validFiles = [];
    for (const file of newFiles) {
      const error = validateFile(file);
      if (error) {
        toast.error(`${file.name}: ${error}`);
      } else {
        validFiles.push(file);
      }
    }

    if (validFiles.length > 0) {
      onChange([...files, ...validFiles]);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (disabled) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      handleFiles(droppedFiles);
    }
  }, [disabled, files, onChange]);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length > 0) {
      handleFiles(selectedFiles);
    }
    e.target.value = '';
  };

  const removeFile = (index) => {
    onChange(files.filter((_, i) => i !== index));
  };

  // Preview local file
  const previewLocalFile = (file) => {
    const ext = file.name.toLowerCase().split('.').pop();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
      // Clean up after a delay
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } else if (ext === 'pdf') {
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  };

  return (
    <div className="space-y-4" data-testid="document-upload-pending">
      {files.length < MAX_FILES && !disabled && (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragActive 
              ? 'border-[#00A6E8] bg-[#00A6E8]/5' 
              : 'border-white/20 hover:border-white/40'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="mx-auto text-slate-400 mb-3" size={32} />
          <p className="text-white font-semibold mb-1">
            Drop files here or click to upload
          </p>
          <p className="text-slate-500 text-sm mb-3">
            PDF, DOC, DOCX, JPG, PNG, XLS (max 10MB, up to {MAX_FILES} files)
          </p>
          <input
            type="file"
            id="pending-file-upload"
            className="hidden"
            multiple
            accept={ALLOWED_EXTENSIONS.join(',')}
            onChange={handleFileSelect}
          />
          <label htmlFor="pending-file-upload">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-[#00A6E8] text-[#00A6E8] cursor-pointer"
              asChild
            >
              <span>Select Files</span>
            </Button>
          </label>
        </div>
      )}

      {files.length > 0 && (
        <div className="space-y-2">
          <p className="text-slate-400 text-xs uppercase tracking-wider font-semibold">
            Files to Upload ({files.length}/{MAX_FILES})
          </p>
          {files.map((file, index) => {
            const ext = file.name.toLowerCase().split('.').pop();
            const canPreview = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
            
            return (
              <div 
                key={index} 
                className="flex items-center justify-between bg-[#1E293B]/50 border border-white/10 rounded-lg p-3"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="text-2xl flex-shrink-0">
                    {getFileIcon(file.name)}
                  </span>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">
                      {file.name}
                    </p>
                    <p className="text-slate-500 text-xs">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {canPreview && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-[#FFA726] hover:text-[#FFA726]/80 hover:bg-[#FFA726]/10 h-8 w-8 p-0"
                      onClick={() => previewLocalFile(file)}
                      title="Preview"
                    >
                      <Eye size={16} />
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-red-400 hover:text-red-300 hover:bg-red-400/10 h-8 w-8 p-0"
                    onClick={() => removeFile(index)}
                  >
                    <X size={16} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
